/**
 * Bell Curve 차트 (AIQ 지수 시각화)
 *
 * 표준 IQ 분포 N(100, 15)의 정규분포 PDF를 SVG path로 그리고,
 * preIndex / postIndex를 곡선 위에 점(+레이블)로 표시합니다.
 *
 * phase:
 *  - 'pre' → preIndex만 표시 (파랑)
 *  - 'post' → postIndex만 표시 (보라)
 *  - 'comparison' → 둘 다 표시 + 두 점 사이 연결 (향상=초록, 하락=빨강)
 */

const PRE_COLOR = '#3b82f6' // blue
const POST_COLOR = '#8b5cf6' // purple

const X_MIN = 40
const X_MAX = 200
const MU = 100
const SIGMA = 15

export interface BellCurveChartProps {
  preIndex: number | null
  postIndex: number | null
  phase: 'pre' | 'post' | 'comparison'
  width?: number
  height?: number
}

/** 정규분포 PDF: φ(x) = 1/(σ√(2π)) * exp(-(x-μ)²/(2σ²)) */
function pdf(x: number, mu: number, sigma: number): number {
  const z = (x - mu) / sigma
  return Math.exp(-0.5 * z * z) / (sigma * Math.sqrt(2 * Math.PI))
}

export function BellCurveChart({
  preIndex,
  postIndex,
  phase,
  width = 520,
  height = 220,
}: BellCurveChartProps) {
  // Plot area insets (leave room for x-axis labels + dot labels)
  const padLeft = 24
  const padRight = 24
  const padTop = 24
  const padBottom = 36

  const plotW = width - padLeft - padRight
  const plotH = height - padTop - padBottom

  // Build sample points of the PDF
  const STEP = 2 // resolution: 81 points across [40, 200]
  const samples: Array<{ x: number; y: number }> = []
  for (let xi = X_MIN; xi <= X_MAX; xi += STEP) {
    samples.push({ x: xi, y: pdf(xi, MU, SIGMA) })
  }
  const yMax = samples.reduce((m, p) => (p.y > m ? p.y : m), 0)

  const xToPx = (x: number) => padLeft + ((x - X_MIN) / (X_MAX - X_MIN)) * plotW
  const yToPx = (y: number) => padTop + plotH - (y / yMax) * plotH

  // Area path (closed to baseline)
  const baselineY = padTop + plotH
  const areaPath = [
    `M ${xToPx(samples[0].x)} ${baselineY}`,
    ...samples.map((p) => `L ${xToPx(p.x).toFixed(2)} ${yToPx(p.y).toFixed(2)}`),
    `L ${xToPx(samples[samples.length - 1].x)} ${baselineY}`,
    'Z',
  ].join(' ')

  // Outline path (stroke only)
  const outlinePath = [
    `M ${xToPx(samples[0].x)} ${yToPx(samples[0].y).toFixed(2)}`,
    ...samples.slice(1).map((p) => `L ${xToPx(p.x).toFixed(2)} ${yToPx(p.y).toFixed(2)}`),
  ].join(' ')

  // Sigma markers (-2σ, -1σ, μ, +1σ, +2σ) — plus 40, 160 for range clarity
  const ticks = [40, 70, 85, 100, 115, 130, 160, 200]

  // Clamp helper
  const clamp = (v: number) => Math.max(X_MIN, Math.min(X_MAX, v))

  // Dot placement (on the curve). Use the actual sampled PDF value at that x.
  type Dot = { x: number; label: string; color: string; rawValue: number }
  const dots: Dot[] = []
  const showPre = (phase === 'pre' || phase === 'comparison') && preIndex != null
  const showPost = (phase === 'post' || phase === 'comparison') && postIndex != null
  if (showPre) {
    dots.push({ x: clamp(preIndex!), label: 'Pre', color: PRE_COLOR, rawValue: preIndex! })
  }
  if (showPost) {
    dots.push({ x: clamp(postIndex!), label: 'Post', color: POST_COLOR, rawValue: postIndex! })
  }

  // Connecting arrow for comparison
  const showArrow = phase === 'comparison' && preIndex != null && postIndex != null
  const arrowColor =
    showArrow && postIndex! > preIndex! ? '#22c55e' : showArrow ? '#ef4444' : '#6b7280'

  return (
    <div className="w-full flex justify-center">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="AIQ 지수 정규분포 차트"
        className="max-w-full"
      >
        {/* Area fill */}
        <path d={areaPath} fill="#c7d2fe" opacity={0.35} />

        {/* Curve outline */}
        <path d={outlinePath} fill="none" stroke="#6366f1" strokeWidth={1.5} opacity={0.7} />

        {/* Baseline */}
        <line
          x1={padLeft}
          y1={baselineY}
          x2={padLeft + plotW}
          y2={baselineY}
          stroke="#cbd5e1"
          strokeWidth={1}
        />

        {/* μ reference line */}
        <line
          x1={xToPx(MU)}
          y1={padTop}
          x2={xToPx(MU)}
          y2={baselineY}
          stroke="#94a3b8"
          strokeWidth={1}
          strokeDasharray="3,3"
          opacity={0.6}
        />

        {/* Tick marks + labels */}
        {ticks.map((t) => {
          const px = xToPx(t)
          const isMu = t === MU
          return (
            <g key={t}>
              <line
                x1={px}
                y1={baselineY}
                x2={px}
                y2={baselineY + 4}
                stroke="#94a3b8"
                strokeWidth={1}
              />
              <text
                x={px}
                y={baselineY + 16}
                fontSize={10}
                fill={isMu ? '#475569' : '#94a3b8'}
                textAnchor="middle"
                fontWeight={isMu ? 'bold' : 'normal'}
              >
                {t}
              </text>
            </g>
          )
        })}

        {/* Arrow marker definition */}
        <defs>
          <marker
            id="bellArrow"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill={arrowColor} />
          </marker>
        </defs>

        {/* Connecting arrow (pre -> post), drawn along baseline for readability */}
        {showArrow && (() => {
          const preX = xToPx(clamp(preIndex!))
          const postX = xToPx(clamp(postIndex!))
          const arrowY = baselineY - 6
          return (
            <line
              x1={preX}
              y1={arrowY}
              x2={postX}
              y2={arrowY}
              stroke={arrowColor}
              strokeWidth={1.5}
              markerEnd="url(#bellArrow)"
              opacity={0.8}
            />
          )
        })()}

        {/* Index dots (on the curve) + labels */}
        {dots.map((d, i) => {
          const dx = xToPx(d.x)
          const dy = yToPx(pdf(d.x, MU, SIGMA))
          // Stagger labels vertically if pre/post are close
          const labelOffset = i === 0 ? -14 : -26
          return (
            <g key={d.label}>
              {/* Drop line from dot to baseline */}
              <line
                x1={dx}
                y1={dy}
                x2={dx}
                y2={baselineY}
                stroke={d.color}
                strokeWidth={1}
                strokeDasharray="2,2"
                opacity={0.5}
              />
              <circle cx={dx} cy={dy} r={6} fill={d.color} opacity={0.95} />
              <text
                x={dx}
                y={dy + labelOffset}
                fontSize={11}
                fontWeight="bold"
                fill={d.color}
                textAnchor="middle"
              >
                {d.label} {Math.round(d.rawValue)}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
